import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from './Layout';
import StatusInfo from '../components/StatusInfo';
import { fmtMoney } from '../utils/format';
import useIsMobile from '../hooks/useIsMobile';

const UPDATE_INFO = [['Обновление данных', 'Данные обновляются ежедневно в 03:00 (ночной опрос клиник).']];

const API = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';
const MONTHS_SHORT = ['', 'янв', 'фев', 'мар', 'апр', 'май', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек'];
const MONTHS_FULL = ['', 'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'];
const PAY_LABEL = { pending: 'Ожидает', paid: 'Выплачено', accepted: 'Принято' };
const payBadgeStyle = (status, isPaid, s) => {
  if (status === 'accepted') return s.badgeTeal;
  if (status === 'paid' || isPaid) return s.badgeBlue;
  return s.badgeGray;
};
const PAYMENT_STATUS_INFO = [
  ['—', 'Текущий месяц ещё не завершился — статус появится после его окончания.'],
  ['Выплачено', 'Refero перевела вам деньги за эту клинику/сеть и период.'],
  ['Принято', 'Чек принят Refero (СМЗ) или акт принят вами (ИП) — выплата полностью закрыта.'],
  ['Ожидает', 'Либо клиника ещё не оплатила Refero за период, либо оплатила недавно и выплата вам скоро будет проведена.'],
];

const subtotal = (c) => parseFloat(c.primary_amount || 0) + parseFloat(c.repeat_amount || 0);
const fmt = fmtMoney;
const toInputDate = (d) => d.toISOString().split('T')[0];
const fmtRu = (str) => { if (!str) return ''; const [y, m, d] = str.split('-'); return `${d}.${m}.${y}`; };
const inRange = (dateStr, from, to) => { const d = (dateStr || '').slice(0, 10); return d >= from && d <= to; };

export default function Dashboard() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [commissions, setCommissions] = useState([]);
  const [daily, setDaily] = useState([]); // N-018: точные по дням начисления (Transaction.commission_amount)
  const [patients, setPatients] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [mode, setMode] = useState('all'); // 'all' | 'range'
  const [dateFrom, setDateFrom] = useState(toInputDate(new Date(Date.now() - 30 * 86400000)));
  const [dateTo, setDateTo] = useState(toInputDate(new Date()));
  const [applied, setApplied] = useState(null); // { from, to }
  const [expanded, setExpanded] = useState({}); // N-022: раскрытие «Последние начисления» по месяцу
  const [verificationStatus, setVerificationStatus] = useState('active');
  const [payoutMonths, setPayoutMonths] = useState([]); // N-065: чеки/акты по выплатам
  const [operations, setOperations] = useState([]); // N-115: закрытые направления по программе «Операции»

  useEffect(() => {
    const h = { Authorization: 'Bearer ' + localStorage.getItem('token') };
    Promise.all([
      axios.get(`${API}/commissions/`, { headers: h }),
      axios.get(`${API}/commissions/daily`, { headers: h }),
      axios.get(`${API}/patients/`, { headers: h }),
      axios.get(`${API}/auth/me`, { headers: h }),
      axios.get(`${API}/commissions/months`, { headers: h }).catch(() => ({ data: [] })),
      axios.get(`${API}/commissions/operations`, { headers: h }).catch(() => ({ data: [] })),
    ]).then(([c, d, p, me, months, ops]) => {
      setCommissions(c.data); setDaily(d.data); setPatients(p.data);
      localStorage.setItem('doctorName', `${me.data.last_name} ${me.data.first_name}`);
      setVerificationStatus(me.data.verification_status);
      setPayoutMonths(months.data);
      setOperations(ops.data);
      setLoaded(true);
    }).catch(() => { localStorage.clear(); window.location.href = '/login'; });
  }, []);

  // N-065: неотправленный чек (СМЗ) или непринятый акт (ИП) за уже выплаченный месяц —
  // мягкое предупреждение, что дальнейшие выплаты могут быть приостановлены.
  const pendingPayoutDoc = payoutMonths.find(m => {
    const doc = m.document;
    if (!doc) return false;
    if (doc.kind === 'receipt') return doc.status === 'not_uploaded' || doc.status === 'rejected';
    return doc.status === 'sent';
  });

  const applyPeriod = () => { if (dateFrom && dateTo) { setApplied({ from: dateFrom, to: dateTo }); setMode('range'); } };
  const resetPeriod = () => { setApplied(null); setMode('all'); };

  if (!loaded) return <Layout><div style={s.muted}>Загрузка…</div></Layout>;

  const hasPeriod = mode === 'range' && applied;
  const periodLabel = hasPeriod ? `${fmtRu(applied.from)} — ${fmtRu(applied.to)}` : 'за всё время';

  // ── агрегаты ──
  const now = new Date();
  const curY = now.getFullYear(), curM = now.getMonth() + 1;
  const prevDate = new Date(curY, curM - 2, 1);
  const prevY = prevDate.getFullYear(), prevM = prevDate.getMonth() + 1;

  const sumForMonth = (y, m) => commissions.filter(c => c.period_year === y && c.period_month === m).reduce((a, c) => a + subtotal(c), 0);
  const earnedThisMonth = sumForMonth(curY, curM);
  const earnedPrevMonth = sumForMonth(prevY, prevM);
  const delta = earnedPrevMonth > 0 ? Math.round((earnedThisMonth - earnedPrevMonth) / earnedPrevMonth * 100) : null;
  const totalAll = commissions.reduce((a, c) => a + subtotal(c), 0);
  // N-115: доход по программе «Операции» — отдельный блок, не смешиваем со стандартной
  // системой (та вся построена на календарных месяцах, «Операции» — нет).
  const totalOperations = operations.reduce((a, o) => a + (o.doctor_amount || 0), 0);
  const reached = patients.filter(p => (p.targets || []).some(t => t.status === 'primary_done')).length;

  // N-018: денежное за период — точная сумма по дням оплаты, а не по пересекающимся месяцам
  const earnedPeriod = hasPeriod
    ? daily.filter(d => d.paid_date >= applied.from && d.paid_date <= applied.to).reduce((a, d) => a + d.amount, 0)
    : earnedThisMonth;
  // дельты срезовых за период (по created_at)
  const patientsDelta = hasPeriod ? patients.filter(p => inRange(p.created_at, applied.from, applied.to)).length : null;
  const reachedDelta = hasPeriod ? patients.filter(p => (p.targets || []).some(t => t.status === 'primary_done') && inRange(p.created_at, applied.from, applied.to)).length : null;

  // ── ряд для графика ──
  const series = [];
  if (hasPeriod) {
    const [fy, fm] = applied.from.split('-').map(Number);
    const [ty, tm] = applied.to.split('-').map(Number);
    let y = fy, m = fm;
    while (y < ty || (y === ty && m <= tm)) {
      series.push({ y, m, v: sumForMonth(y, m) });
      m++; if (m > 12) { m = 1; y++; }
    }
  } else {
    for (let i = 11; i >= 0; i--) {
      const d = new Date(curY, curM - 1 - i, 1);
      series.push({ y: d.getFullYear(), m: d.getMonth() + 1, v: sumForMonth(d.getFullYear(), d.getMonth() + 1) });
    }
  }

  const metrics = [
    { label: hasPeriod ? 'Заработано за период' : 'Заработано за месяц', value: `${fmt(earnedPeriod)} ₽`, hero: true, delta: hasPeriod ? null : delta, href: '/commissions', wide: true },
    { label: 'Пациентов', value: patients.length, delta: patientsDelta, href: '/patients' },
    { label: 'Дошли', value: reached, delta: reachedDelta, href: '/patients?status=reached' },
    { label: 'Доход за всё время (стандарт)', value: `${fmt(totalAll)} ₽`, href: '/commissions', wide: true },
    { label: 'Доход за всё время (операции)', value: `${fmt(totalOperations)} ₽`, href: '/commissions?tab=operations', wide: true },
  ];

  // N-022: группируем по месяцу — платим одной суммой, детализация по клиникам по клику
  const recentGrouped = (() => {
    const map = new Map();
    for (const c of commissions) {
      const key = `${c.period_year}-${c.period_month}`;
      if (!map.has(key)) map.set(key, { key, period_year: c.period_year, period_month: c.period_month, total: 0, statuses: new Set(), items: [], monthEnded: c.month_ended });
      const g = map.get(key);
      g.total += subtotal(c);
      g.statuses.add(c.payment_status);
      g.items.push(c);
    }
    for (const g of map.values()) {
      g.groupStatus = g.statuses.has('pending') ? 'pending' : (g.statuses.size === 1 && g.statuses.has('accepted') ? 'accepted' : 'paid');
    }
    return [...map.values()]
      .sort((a, b) => (b.period_year - a.period_year) || (b.period_month - a.period_month))
      .slice(0, 6);
  })();

  return (
    <Layout>
      <div style={{ ...s.headerRow, ...(isMobile ? s.headerRowMobile : {}) }}>
        <h1 style={s.h1}>Дашборд<StatusInfo items={UPDATE_INFO} /></h1>
        <div style={s.filterBar}>
          <button style={{ ...s.modeBtn, ...(mode === 'all' ? s.modeBtnActive : {}) }} onClick={resetPeriod}>Всё время</button>
          <input type="date" value={dateFrom} max={dateTo} onChange={e => setDateFrom(e.target.value)} style={s.dateInput} />
          <span style={s.dateSep}>—</span>
          <input type="date" value={dateTo} min={dateFrom} max={toInputDate(new Date())} onChange={e => setDateTo(e.target.value)} style={s.dateInput} />
          <button style={s.applyBtn} onClick={applyPeriod}>Применить</button>
        </div>
      </div>

      {verificationStatus !== 'active' && (
        <div style={s.verifyHint} onClick={() => navigate('/profile')}>
          {verificationStatus === 'pending'
            ? 'Ваша выписка ЕГИСЗ на проверке — как только подтвердим, сможете добавлять пациентов.'
            : verificationStatus === 'rejected'
              ? 'Верификация отклонена — загрузите документ повторно в профиле, чтобы добавлять пациентов.'
              : 'Загрузите выписку ЕГИСЗ в профиле, чтобы добавлять пациентов.'}
          {' '}Перейти в профиль →
        </div>
      )}

      {pendingPayoutDoc && (
        <div style={s.verifyHint} onClick={() => navigate('/commissions')}>
          {pendingPayoutDoc.document.kind === 'receipt'
            ? `Загрузите чек за ${MONTHS_FULL[pendingPayoutDoc.period_month]} ${pendingPayoutDoc.period_year} — без него дальнейшие выплаты могут быть приостановлены.`
            : `Примите акт за ${MONTHS_FULL[pendingPayoutDoc.period_month]} ${pendingPayoutDoc.period_year} — без этого дальнейшие выплаты могут быть приостановлены.`}
          {' '}Перейти в комиссии →
        </div>
      )}

      {hasPeriod && (
        <div style={s.hint}>
          Пациенты и дошедшие — текущее состояние, в скобках прирост за период.
          Доход — точно за {periodLabel}.
        </div>
      )}

      <div style={{ ...s.metrics, ...(isMobile ? s.metricsMobile : {}) }}>
        {metrics.map(m => (
          <div key={m.label} style={{ ...s.metric, ...(m.hero ? s.metricHero : {}), ...(m.href ? s.metricClickable : {}), ...(isMobile && m.wide ? s.metricWideMobile : {}) }}
            onClick={m.href ? () => navigate(m.href) : undefined}>
            <div style={{ ...s.metricLabel, ...(m.hero ? { color: 'rgba(255,255,255,0.85)' } : {}) }}>
              {m.label}{m.href && <span style={{ ...s.arrow, ...(m.hero ? { color: 'rgba(255,255,255,0.7)' } : {}) }}>→</span>}
            </div>
            <div style={{ ...s.metricValue, ...(m.hero ? { color: '#fff' } : {}) }}>{m.value}</div>
            {m.hero && m.delta != null && (
              <div style={s.deltaBadge}>{m.delta >= 0 ? '+' : ''}{m.delta}% к прошлому месяцу</div>
            )}
            {!m.hero && m.delta != null && (
              <div style={s.deltaPlain}>+{m.delta} за период</div>
            )}
          </div>
        ))}
      </div>

      <div style={s.chartCard}>
        <div style={s.cardTitle}>Доход по месяцам</div>
        <AreaChart series={series} />
      </div>

      <div style={s.tableCard}>
        <div style={s.cardTitle}>Последние начисления</div>
        <div style={isMobile ? s.scrollX : undefined}>
          <div style={{ minWidth: isMobile ? 520 : 'auto' }}>
            <div style={{ ...s.thead, gridTemplateColumns: expCols }}>
              <div /><div>Период</div><div>Сумма врачу</div>
              <div>Статус<StatusInfo items={PAYMENT_STATUS_INFO} /></div>
            </div>
            {recentGrouped.length === 0 && <div style={s.empty}>Начислений пока нет</div>}
            {recentGrouped.map(g => (
              <div key={g.key}>
                <div style={{ ...s.row, gridTemplateColumns: expCols, cursor: 'pointer' }} onClick={() => setExpanded(p => ({ ...p, [g.key]: !p[g.key] }))}>
                  <div style={s.expandArrow}>{expanded[g.key] ? '▲' : '▼'}</div>
                  <div>{MONTHS_FULL[g.period_month]} {g.period_year}</div>
                  <div style={s.money}>{fmt(g.total)} ₽</div>
                  <div>
                    {g.monthEnded
                      ? <span style={{ ...s.badge, ...payBadgeStyle(g.groupStatus, g.groupStatus !== 'pending', s) }}>{PAY_LABEL[g.groupStatus]}</span>
                      : <span style={s.faint}>—</span>}
                  </div>
                </div>
                {expanded[g.key] && g.items.map(c => (
                  <div key={c.id} style={{ ...s.row, gridTemplateColumns: expCols, background: '#fafcfe' }}>
                    <div />
                    <div style={s.strong}>{c.target_name}</div>
                    <div style={s.money}>{fmt(subtotal(c))} ₽</div>
                    <div>
                      {c.month_ended
                        ? <span style={{ ...s.badge, ...payBadgeStyle(c.payment_status, c.is_paid, s) }}>{PAY_LABEL[c.payment_status] || (c.is_paid ? 'Выплачено' : 'Ожидает')}</span>
                        : <span style={s.faint}>—</span>}
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>
    </Layout>
  );
}

// ── Лёгкий area-чарт на SVG, без зависимостей, с тултипом при наведении ──
function AreaChart({ series }) {
  const [hover, setHover] = useState(null);
  const W = 760, H = 240, padL = 48, padR = 16, padT = 16, padB = 28;
  const max = Math.max(1, ...series.map(p => p.v));
  const niceMax = Math.ceil(max / 2000) * 2000 || 2000;
  const plotW = W - padL - padR, plotH = H - padT - padB;
  const x = (i) => padL + (series.length === 1 ? plotW / 2 : (i / (series.length - 1)) * plotW);
  const y = (v) => padT + plotH - (v / niceMax) * plotH;

  const pts = series.map((p, i) => `${x(i)},${y(p.v)}`);
  const linePath = 'M' + pts.join(' L');
  const areaPath = `${linePath} L${x(series.length - 1)},${padT + plotH} L${x(0)},${padT + plotH} Z`;
  const ticks = [0, niceMax / 4, niceMax / 2, (niceMax * 3) / 4, niceMax];
  const hp = hover != null ? series[hover] : null;

  return (
    <div style={{ position: 'relative' }}>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', display: 'block' }}>
        <defs>
          <linearGradient id="areaFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#2EC4B6" stopOpacity="0.28" />
            <stop offset="100%" stopColor="#2EC4B6" stopOpacity="0.02" />
          </linearGradient>
        </defs>
        {ticks.map((t, i) => (
          <g key={i}>
            <line x1={padL} y1={y(t)} x2={W - padR} y2={y(t)} stroke="#EEF2F7" strokeWidth="1" />
            <text x={padL - 8} y={y(t) + 4} textAnchor="end" fontSize="11" fill="#94A3B8">{t === 0 ? '0' : `${(t / 1000)}т`}</text>
          </g>
        ))}
        <path d={areaPath} fill="url(#areaFill)" />
        <path d={linePath} fill="none" stroke="#2EC4B6" strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />
        {hp && <line x1={x(hover)} y1={padT} x2={x(hover)} y2={padT + plotH} stroke="#2EC4B6" strokeWidth="1" strokeDasharray="4 4" opacity="0.5" />}
        {series.map((p, i) => (
          <circle key={i} cx={x(i)} cy={y(p.v)} r={hover === i ? 5 : 3} fill={hover === i ? '#2EC4B6' : '#fff'} stroke="#2EC4B6" strokeWidth="2" />
        ))}
        {series.map((p, i) => (
          <text key={i} x={x(i)} y={H - 8} textAnchor="middle" fontSize="11" fill={hover === i ? '#149e90' : '#94A3B8'} fontWeight={hover === i ? 700 : 400}>{MONTHS_SHORT[p.m]}</text>
        ))}
        {/* прозрачные зоны наведения по столбцам */}
        {series.map((p, i) => (
          <rect key={i} x={x(i) - plotW / series.length / 2} y={padT} width={plotW / series.length} height={plotH}
            fill="transparent" onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)} style={{ cursor: 'pointer' }} />
        ))}
      </svg>
      {hp && (
        <div style={{
          position: 'absolute', left: `${(x(hover) / W) * 100}%`, top: `${(y(hp.v) / H) * 100}%`,
          transform: 'translate(-50%, -125%)', pointerEvents: 'none',
          background: '#1B2B4B', color: '#fff', borderRadius: 10, padding: '8px 12px', whiteSpace: 'nowrap',
          boxShadow: '0 6px 20px rgba(27,43,75,0.25)', zIndex: 2,
        }}>
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.7)', marginBottom: 2 }}>{MONTHS_FULL[hp.m]} {hp.y}</div>
          <div style={{ fontSize: 15, fontWeight: 700 }}>{fmt(hp.v)} ₽</div>
        </div>
      )}
    </div>
  );
}

const expCols = '24px 1.6fr 1.2fr 1fr';
const s = {
  h1: { color: '#1B2B4B', fontSize: 28, fontWeight: 800, margin: 0 },
  muted: { color: '#64748b', fontSize: 14 },
  faint: { color: '#94a3b8', fontSize: 13 },
  headerRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 20 },
  headerRowMobile: { flexDirection: 'column', alignItems: 'stretch' },
  filterBar: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
  scrollX: { overflowX: 'auto' },
  modeBtn: { background: '#fff', color: '#64748b', border: '1px solid #d7dee8', padding: '8px 16px', borderRadius: 10, fontSize: 13, cursor: 'pointer', fontWeight: 500 },
  modeBtnActive: { background: '#2EC4B6', color: '#fff', border: '1px solid #2EC4B6' },
  dateInput: { background: '#fff', color: '#1B2B4B', border: '1px solid #d7dee8', borderRadius: 10, padding: '7px 10px', fontSize: 13, outline: 'none' },
  dateSep: { color: '#94a3b8', fontSize: 13 },
  applyBtn: { background: '#1B2B4B', color: '#fff', border: 'none', padding: '8px 16px', borderRadius: 10, fontSize: 13, cursor: 'pointer', fontWeight: 600 },
  hint: { fontSize: 12, color: '#64748b', background: '#fff', borderRadius: 10, padding: '10px 16px', marginBottom: 20, borderLeft: '3px solid #2EC4B6' },
  verifyHint: { fontSize: 13, color: '#92400e', background: '#fffbeb', borderRadius: 10, padding: '12px 16px', marginBottom: 20, borderLeft: '3px solid #f59e0b', cursor: 'pointer', fontWeight: 500 },
  metrics: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 20 },
  metricsMobile: { gridTemplateColumns: 'repeat(2, 1fr)' },
  metricWideMobile: { gridColumn: '1 / -1' },
  metric: { background: '#fff', borderRadius: 16, padding: '20px 22px', boxShadow: '0 4px 16px rgba(27,43,75,0.06)' },
  metricClickable: { cursor: 'pointer' },
  metricHero: { background: 'linear-gradient(135deg, #2EC4B6, #21a99c)', boxShadow: '0 8px 24px rgba(46,196,182,0.35)' },
  metricLabel: { fontSize: 13, color: '#64748b', marginBottom: 10, fontWeight: 500, display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  arrow: { color: '#94a3b8', fontSize: 14 },
  metricValue: { fontSize: 26, fontWeight: 800, color: '#1B2B4B', whiteSpace: 'nowrap' },
  deltaBadge: { marginTop: 10, display: 'inline-block', background: 'rgba(255,255,255,0.2)', color: '#fff', fontSize: 12, fontWeight: 600, padding: '3px 10px', borderRadius: 20 },
  heroNote: { marginTop: 8, fontSize: 11, color: 'rgba(255,255,255,0.8)' },
  deltaPlain: { marginTop: 8, fontSize: 12, color: '#149e90', fontWeight: 600 },
  chartCard: { background: '#fff', borderRadius: 16, padding: 24, boxShadow: '0 4px 16px rgba(27,43,75,0.06)', marginBottom: 20 },
  tableCard: { background: '#fff', borderRadius: 16, padding: 24, boxShadow: '0 4px 16px rgba(27,43,75,0.06)' },
  cardTitle: { fontSize: 17, fontWeight: 700, color: '#1B2B4B', marginBottom: 16 },
  thead: { display: 'grid', padding: '0 4px 10px', color: '#94a3b8', fontSize: 12, fontWeight: 600, textTransform: 'uppercase', borderBottom: '1px solid #eef2f7' },
  row: { display: 'grid', padding: '14px 4px', borderBottom: '1px solid #f4f7fb', fontSize: 14, color: '#1B2B4B', alignItems: 'center' },
  strong: { fontWeight: 600 },
  expandArrow: { color: '#94a3b8', fontSize: 10 },
  money: { color: '#149e90', fontWeight: 700 },
  badge: { padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600 },
  badgeTeal: { background: '#e9faf7', color: '#149e90' },
  badgeGray: { background: '#f1f5f9', color: '#64748b' },
  badgeBlue: { background: '#e0edff', color: '#2563eb' },
  empty: { color: '#94a3b8', textAlign: 'center', padding: '28px 0', fontSize: 14 },
};
